import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { filterToCriteria, ILeadViewFilter } from '../../../../entities/LeadView/useLeadView';
import { ILeadServedOutcomeView } from '../../../../viewModels/leads/interfaces';

// Default filter focusing on calls with transcriptions from today
export const DefaultCallAnalysisFilter: ILeadViewFilter = {
	date: {
		start: new Date(),
		end: new Date(),
	},
	transcriptionStatus: {
		id: 'TranscriptionComplete',
		label: 'Transcribed',
		dataContext: Api.TranscriptionStatusFilter.TranscriptionComplete,
	},
};

export const DefaultCallAnalysisSort: Api.ISortDescriptor = {
	sort: 'desc',
	sortBy: 'creationDate',
};

interface IPagedCollection<T> {
	pageToken?: string;
	totalCount?: number;
	values: T[];
}

export const useCallAnalysisSearch = (initialFilter?: ILeadViewFilter) => {
	const [filter, setFilter] = React.useState<ILeadViewFilter>(initialFilter || DefaultCallAnalysisFilter);
	const [sort, setSort] = React.useState<Api.ISortDescriptor>(DefaultCallAnalysisSort);
	const userSession = useUserSession();
	// Use infinite query for load more functionality
	const callQuery = useInfiniteQuery({
		queryKey: ['call-analysis-infinite', JSON.stringify(filter), sort],
		queryFn: async ({ pageParam = undefined }): Promise<IPagedCollection<ILeadServedOutcomeView>> => {
			const criteria = filterToCriteria(filter);

			return new Promise((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceWithOperationResults<IPagedCollection<ILeadServedOutcomeView>>(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: 'LeadServed/filter',
						queryParams: {
							pageSize: '25',
							...(pageParam && { pageToken: pageParam }),
						},
					}),
					'POST',
					{ criteria },
					result => {
						if (result.success && result.value?.values) {
							resolve(result.value);
						} else {
							reject(new Error(result.systemMessage || 'Failed to fetch call data'));
						}
					},
					error => {
						reject(new Error(error.systemMessage || 'API call failed'));
					}
				);
			});
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		enabled: true,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		initialPageParam: undefined,
	});

	// Transform lead served data to call analysis format
	const transformToCallAnalysisData = React.useCallback((leadServed: ILeadServedOutcomeView) => {
		const transformed = {
			phoneCall: leadServed.phoneCall,
			company: leadServed.company,
			contact: leadServed.contact,
			creator: leadServed.creator, // SDR who made the call
			interaction: leadServed.interaction, // Contains outcome data
			hasTranscription: !!leadServed.phoneCall?.transcriptionId,
			// Will be extended with actual analysis status tracking
			analysisStatus: undefined as 'pending' | 'completed' | 'failed' | undefined,
		};
		return transformed;
	}, []);

	const calls = React.useMemo(() => {
		const allCalls: ILeadServedOutcomeView[] = [];
		callQuery.data?.pages.forEach(page => {
			allCalls.push(...page.values);
		});
		return allCalls.map(transformToCallAnalysisData);
	}, [callQuery.data, transformToCallAnalysisData]);

	const isLoading = callQuery.isPending || callQuery.isFetchingNextPage;

	// Load more function for infinite scroll
	const loadMore = React.useCallback(
		(shouldNotTriggerLoading?: boolean) => {
			if (!shouldNotTriggerLoading && callQuery.hasNextPage && !callQuery.isFetchingNextPage) {
				callQuery.fetchNextPage();
			}
		},
		[callQuery]
	);

	const refresh = React.useCallback(async () => {
		callQuery.refetch();
	}, [callQuery]);

	return {
		calls,
		filter,
		setFilter,
		sort,
		setSort,
		isLoading,
		isEmpty: !callQuery.data?.pages?.[0]?.values?.length,
		loadMore,
		refresh,
		hasNextPage: callQuery.hasNextPage,
		isFetchingNextPage: callQuery.isFetchingNextPage,
		// Expose React Query state for debugging
		queryState: {
			isError: callQuery.isError,
			error: callQuery.error,
			isSuccess: callQuery.isSuccess,
		},
	};
};
